import type { Division } from '@/lib/types/deck-builder';
import { Country } from '@/lib/types/deck-builder';

const COUNTRY_LABELS: Record<Country, string> = {
  [Country.BEL]: 'Belgium',
  [Country.CAN]: 'Canada',
  [Country.FR]: 'France',
  [Country.UK]: 'United Kingdom',
  [Country.RFA]: 'West Germany',
  [Country.US]: 'United States',
  [Country.SOV]: 'Soviet Union',
  [Country.DDR]: 'East Germany',
  [Country.ESP]: 'Spain',
  [Country.POL]: 'Poland',
  [Country.TCH]: 'Czechoslovakia',
  [Country.NL]: 'Netherlands',
  [Country.LUX]: 'Luxembourg',
  [Country.DK]: 'Denmark',
};

export interface DivisionSelectGroup {
  allianceKey: string;
  country: Country;
  /** Header for SelectLabel, e.g. "NATO — United States" */
  label: string;
  divisions: Division[];
}

/** Short coalition label for UI (NATO / PACT / fallback). */
export function formatAllianceLabel(alliance: string): string {
  if (alliance.includes('NATO')) return 'NATO';
  if (alliance.includes('PACT')) return 'PACT';
  const parts = alliance.split('/');
  return parts[parts.length - 1] || alliance;
}

export function formatCountryLabel(country: Country): string {
  return COUNTRY_LABELS[country] ?? country;
}

function allianceSortRank(alliance: string): number {
  if (alliance.includes('NATO')) return 0;
  if (alliance.includes('PACT')) return 1;
  return 2;
}

/**
 * Groups divisions by (alliance, country), sorted NATO then PACT then other,
 * then by country display name; divisions within a group are sorted by name.
 */
export function groupDivisionsForSelect(divisions: Division[]): DivisionSelectGroup[] {
  const bucket = new Map<string, { alliance: string; country: Country; divisions: Division[] }>();

  for (const d of divisions) {
    const key = `${d.alliance}\0${d.country}`;
    let g = bucket.get(key);
    if (!g) {
      g = { alliance: d.alliance, country: d.country, divisions: [] };
      bucket.set(key, g);
    }
    g.divisions.push(d);
  }

  for (const g of bucket.values()) {
    g.divisions.sort((a, b) => a.name.localeCompare(b.name));
  }

  const groups: DivisionSelectGroup[] = Array.from(bucket.values()).map((g) => ({
    allianceKey: g.alliance,
    country: g.country,
    label: `${formatAllianceLabel(g.alliance)} — ${formatCountryLabel(g.country)}`,
    divisions: g.divisions,
  }));

  groups.sort((a, b) => {
    const ra = allianceSortRank(a.allianceKey);
    const rb = allianceSortRank(b.allianceKey);
    if (ra !== rb) return ra - rb;
    const byCountry = formatCountryLabel(a.country).localeCompare(formatCountryLabel(b.country));
    if (byCountry !== 0) return byCountry;
    return a.allianceKey.localeCompare(b.allianceKey);
  });

  return groups;
}

function divisionMatchesFilter(
  div: Division,
  group: DivisionSelectGroup,
  q: string
): boolean {
  if (div.name.toLowerCase().includes(q)) return true;
  if (div.descriptor.toLowerCase().includes(q)) return true;
  if (String(div.country).toLowerCase().includes(q)) return true;
  if (formatCountryLabel(div.country).toLowerCase().includes(q)) return true;
  if (formatAllianceLabel(div.alliance).toLowerCase().includes(q)) return true;
  if (group.label.toLowerCase().includes(q)) return true;
  return false;
}

/**
 * Client-side filter for grouped division pickers. Empty `query` returns `groups` unchanged.
 * `alwaysIncludeDescriptor` keeps the current selection visible even when it does not match the query.
 */
export function filterDivisionGroups(
  groups: DivisionSelectGroup[],
  query: string,
  options?: { alwaysIncludeDescriptor?: string }
): DivisionSelectGroup[] {
  const q = query.trim().toLowerCase();
  if (!q) return groups;

  const pin = options?.alwaysIncludeDescriptor;
  return groups
    .map((g) => ({
      ...g,
      divisions: g.divisions.filter((div) => {
        if (pin && div.descriptor === pin) return true;
        return divisionMatchesFilter(div, g, q);
      }),
    }))
    .filter((g) => g.divisions.length > 0);
}
